import { ApiError, api } from '../lib/api';
import { sceneFieldPayload } from './workspaceTimelineActions';
import type { EntityDocument, EntitySummary, TiptapDoc } from '../types/entities';

import type { WorkspaceSaveState } from '../types/workspace';

type WorkspaceDraftStore = {
  activeEntity: EntityDocument | null;
  entities: EntitySummary[];
  saveState: WorkspaceSaveState;
  error: string;
  refreshActiveVersionMetadata(entityId?: string): Promise<void>;
  loadEntities(): Promise<void>;
  scheduleStoryTimelineRefresh(): void;
  loadSceneConflicts(sceneId?: string): Promise<void>;
};

type EntityDraft = {
  title: string;
  summary: string | null;
  contentDoc: TiptapDoc;
  fieldValues: Record<string, unknown>;
};

export const workspaceDraftActions = {
  async checkpointActive(this: WorkspaceDraftStore, reason: 'entity-switch' | 'manual-recovery' = 'entity-switch') {
    if (!this.activeEntity) return;
    const entity = this.activeEntity;
    const result = await api<{ created: boolean; contentRevision: number; autosaveRevision: number }>(
      `/api/v1/entities/${entity.id}/working-version/checkpoints`,
      {
        method: 'POST',
        body: JSON.stringify({
          versionId: entity.workingVersion.id,
          expectedRevision: entity.workingVersion.revision,
          reason
        })
      }
    );
    if (this.activeEntity === entity && result.created) {
      this.activeEntity.workingVersion.lastCheckpointRevision = result.autosaveRevision;
      this.activeEntity.workingVersion.contentRevision = result.contentRevision + 1;
      await this.refreshActiveVersionMetadata(entity.id);
      if (this.activeEntity === entity) {
        try {
          entity.changeToken = (await api<{ token: string }>(`/api/v1/entities/${entity.id}/change-state?includeHistory=${entity.historyLoaded === true}`)).token;
        } catch { entity.changeToken = undefined; }
      }
    }
    return result;
  },
  checkpointBeacon(this: WorkspaceDraftStore) {
    if (!this.activeEntity) return;
    const entity = this.activeEntity;
    navigator.sendBeacon(
      `/api/v1/entities/${entity.id}/working-version/checkpoints`,
      new Blob([JSON.stringify({
        versionId: entity.workingVersion.id,
        expectedRevision: entity.workingVersion.revision,
        reason: 'entity-switch'
      })], { type: 'application/json' })
    );
  },
  markDirty(this: WorkspaceDraftStore) {
    if (this.saveState !== 'saving') this.saveState = 'dirty';
  },
  async saveDraft(this: WorkspaceDraftStore, draft: EntityDraft) {
    if (!this.activeEntity || this.saveState === 'saving') return;
    const entityId = this.activeEntity.id;
    const version = this.activeEntity.workingVersion;
    // Derived overviews depend on titles and fields, not the editor document.
    // Capture this before the request and before replacing the saved version.
    const overviewChanged = draft.title !== this.activeEntity.title
      || JSON.stringify(draft.fieldValues) !== JSON.stringify(version.fieldValues);
    this.saveState = 'saving';
    try {
      const result = await api<{ revision: number; changed: boolean; contentHash: string; savedAt: string }>(
        `/api/v1/entities/${entityId}/working-version`,
        {
          method: 'PUT',
          body: JSON.stringify({
            versionId: version.id,
            baseRevision: version.revision,
            title: draft.title,
            summary: draft.summary,
            contentDoc: draft.contentDoc,
            fieldValues: this.activeEntity?.templateCategory === 'szene' ? sceneFieldPayload(draft.fieldValues) : draft.fieldValues,
            reason: 'debounce'
          })
        }
      );
      if (this.activeEntity?.id !== entityId) return;
      this.activeEntity.title = draft.title;
      this.activeEntity.summary = draft.summary;
      this.activeEntity.workingVersion.title = draft.title;
      this.activeEntity.workingVersion.summary = draft.summary;
      this.activeEntity.workingVersion.contentDoc = JSON.parse(JSON.stringify(draft.contentDoc)) as TiptapDoc;
      this.activeEntity.workingVersion.fieldValues = JSON.parse(JSON.stringify(draft.fieldValues)) as Record<string, unknown>;
      this.activeEntity.workingVersion.revision = result.revision;
      this.activeEntity.workingVersion.contentHash = result.contentHash;
      this.activeEntity.workingVersion.updatedAt = result.savedAt;
      const summary = this.entities.find((entity) => entity.id === entityId);
      if (summary) {
        summary.title = draft.title;
        summary.summary = draft.summary;
        summary.fieldValues = JSON.parse(JSON.stringify(draft.fieldValues)) as Record<string, unknown>;
      }
      this.saveState = 'saved';
      if (!result.changed || !overviewChanged) return;
      if (this.activeEntity?.templateCategory === 'orte') await this.loadEntities();
      if (this.activeEntity?.templateCategory === 'szene' || this.activeEntity?.templateCategory === 'kapitel') {
        // Chapter ↔ scene links are synchronized server-side. Refresh the
        // overview immediately so both directions are visible after a save.
        await this.loadEntities();
        this.scheduleStoryTimelineRefresh();
      }
      if (this.activeEntity?.templateCategory === 'szene') await this.loadSceneConflicts();
    } catch (error) {
      this.saveState = error instanceof ApiError && error.problem.code === 'REVISION_CONFLICT' ? 'conflict' : 'offline';
      this.error = error instanceof Error ? error.message : 'Speichern fehlgeschlagen.';
      throw error;
    }
  }
};
